import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Truck, Camera, Flag, FlagOff, Trash2, Loader2, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import AuthImage from "@/components/app/AuthImage";

function InspectionBlock({ insp, jobId, onDeleted, canDelete }) {
  const isPre = insp.phase === "pre";
  const headerColor = isPre ? "bg-sky-50 border-sky-200 text-sky-900" : "bg-emerald-50 border-emerald-200 text-emerald-900";

  const remove = async () => {
    if (!confirm("Eliminare questa ispezione?")) return;
    try { await api.delete(`/jobs/${jobId}/inspections/${insp.id}`); onDeleted?.(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className={`rounded-xl border p-4 ${headerColor}`} data-testid={`inspection-${insp.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {isPre ? <Flag className="w-4 h-4" /> : <FlagOff className="w-4 h-4" />}
          <span className="font-display font-bold uppercase text-xs tracking-widest">
            {isPre ? "Pre-Partenza" : "Post-Ritorno"}
          </span>
          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest bg-white/70 border border-current/20 rounded-full px-2 py-0.5" data-testid={`inspection-${insp.phase}-sent-badge`}>
            <CheckCircle2 className="w-2.5 h-2.5" /> Inviato
          </span>
        </div>
        <div className="text-[11px] font-mono opacity-75">
          {insp.timestamp ? new Date(insp.timestamp).toLocaleString("it-IT") : ""}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">Caposquadra</div>
          <div className="font-semibold">{insp.foreman_name || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">Km {isPre ? "partenza" : "arrivo"}</div>
          <div className="font-mono font-bold">{insp.km?.toLocaleString?.("it-IT") ?? insp.km}</div>
        </div>
        {insp.phase === "post" && insp.diesel_cost_eur > 0 && (
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">Diesel speso</div>
            <div className="font-mono font-bold">{new Intl.NumberFormat("it-IT",{style:"currency",currency:"EUR"}).format(insp.diesel_cost_eur)}</div>
          </div>
        )}
      </div>
      {insp.note && <div className="text-xs italic mt-2">{insp.note}</div>}
      {(insp.photos || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {insp.photos.map((p) => (
            <button key={p.id}
               type="button"
               onClick={async () => {
                 try {
                   const r = await api.get(`/jobs/${jobId}/inspections/${insp.id}/photos/${p.id}/download`, { responseType: "blob" });
                   const u = URL.createObjectURL(r.data);
                   window.open(u, "_blank");
                   setTimeout(() => URL.revokeObjectURL(u), 60000);
                 } catch (err) { toast.error(formatApiError(err)); }
               }}
               className="block">
              <AuthImage path={`/jobs/${jobId}/inspections/${insp.id}/photos/${p.id}/download`}
                         className="w-20 h-20 rounded-lg object-cover border border-slate-200 cursor-zoom-in" />
            </button>
          ))}
        </div>
      )}
      {canDelete && (
        <button onClick={remove} className="mt-3 text-xs text-rose-700 hover:text-rose-900 font-semibold inline-flex items-center gap-1" data-testid={`inspection-${insp.id}-delete`}>
          <Trash2 className="w-3.5 h-3.5" /> Elimina (admin)
        </button>
      )}
    </div>
  );
}

function InspectionForm({ phase, jobId, onDone, defaultName }) {
  const [form, setForm] = useState({ foreman_name: defaultName || "", km: "", diesel_cost_eur: "", note: "" });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.foreman_name) return toast.error("Inserisci nome caposquadra");
    if (!form.km) return toast.error("Inserisci i km");
    if (phase === "post" && !form.diesel_cost_eur) return toast.error("Inserisci € diesel speso");
    if (!files.length) return toast.error("Foto del furgone obbligatoria: scatta almeno una foto");
    if (!confirm(phase === "pre"
      ? "Una volta inviati i dati di partenza NON potrai più modificarli. Continuare?"
      : "Una volta inviati i dati di ritorno NON potrai più modificarli. Continuare?")) return;
    setSaving(true);
    try {
      const fd = new FormData();
      (files || []).forEach((f) => fd.append("files", f));
      const params = {
        phase,
        foreman_name: form.foreman_name,
        km: Number(form.km),
        note: form.note || "",
      };
      if (phase === "post") params.diesel_cost_eur = Number(form.diesel_cost_eur) || 0;
      await api.post(`/jobs/${jobId}/inspections`, fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(phase === "pre" ? "Partenza INVIATA ✓" : "Ritorno INVIATO ✓");
      setForm({ foreman_name: defaultName || "", km: "", diesel_cost_eur: "", note: "" });
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const btnColor = phase === "pre" ? "bg-sky-600 hover:bg-sky-700" : "bg-emerald-600 hover:bg-emerald-700";

  return (
    <form onSubmit={submit} className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3" data-testid={`inspection-form-${phase}`}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-700">
        {phase === "pre" ? <Flag className="w-3.5 h-3.5" /> : <FlagOff className="w-3.5 h-3.5" />}
        {phase === "pre" ? "Compila PRIMA di prendere il furgone" : "Compila AL RIENTRO del furgone"}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Caposquadra / Modello</Label>
          <Input value={form.foreman_name} onChange={(e) => setForm({ ...form, foreman_name: e.target.value })} required
                 data-testid={`inspection-${phase}-foreman-input`} />
        </div>
        <div>
          <Label className="text-xs">Km {phase === "pre" ? "partenza" : "attuali"}</Label>
          <Input type="number" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} required
                 data-testid={`inspection-${phase}-km-input`} />
        </div>
      </div>
      {phase === "post" && (
        <div>
          <Label className="text-xs">€ Diesel speso</Label>
          <Input type="number" step="0.01" value={form.diesel_cost_eur}
                 onChange={(e) => setForm({ ...form, diesel_cost_eur: e.target.value })}
                 placeholder="Es: 85.50" required
                 data-testid={`inspection-${phase}-diesel-input`} />
        </div>
      )}
      <div>
        <Label className="text-xs">Note / stato furgone</Label>
        <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder={phase === "pre" ? "Es: Pieno OK, nessun danno visibile" : "Es: Rifornito, pulito"}
                  data-testid={`inspection-${phase}-note-input`} />
      </div>
      <div>
        <Label className="text-xs flex items-center gap-1">
          <Camera className="w-3.5 h-3.5" /> Foto furgone {phase === "pre" ? "PRIMA" : "DOPO"} <span className="text-rose-600">* obbligatoria</span>
        </Label>
        <input ref={fileRef} type="file" accept="image/*" multiple
               onChange={(e) => setFiles(Array.from(e.target.files || []))}
               className="block w-full text-sm mt-1" data-testid={`inspection-${phase}-files-input`} />
        {files.length > 0 && <div className="text-[11px] text-slate-500 mt-1">{files.length} foto selezionate</div>}
      </div>
      <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
        ⚠️ Dopo l'invio NON potrai più modificare questa ispezione.
      </div>
      <Button type="submit" disabled={saving} className={`w-full ${btnColor} text-white`} data-testid={`inspection-${phase}-save-button`}>
        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        <Plus className="w-4 h-4 mr-1" /> Invia {phase === "pre" ? "Partenza" : "Ritorno"}
      </Button>
    </form>
  );
}

export default function InspectionPanel({ job, canEdit, onChanged }) {
  const { user } = useAuth();
  const inspections = job.inspections || [];
  const refresh = async () => {
    const { data } = await api.get(`/jobs/${job.id}`);
    onChanged?.(data);
  };
  const hasPre = inspections.some((i) => i.phase === "pre");
  const hasPost = inspections.some((i) => i.phase === "post");
  const isCompleted = job.status === "completato";

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="inspection-panel">
      <h3 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-4">
        <Truck className="w-4 h-4 text-sky-600" /> Ispezione Furgone
        <span className="text-xs font-mono text-slate-400">({inspections.length}/2)</span>
      </h3>

      {inspections.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2 mb-4">
          {inspections.map((i) => (
            <InspectionBlock
              key={i.id}
              insp={i}
              jobId={job.id}
              onDeleted={refresh}
              canDelete={user?.role === "admin"}
            />
          ))}
        </div>
      )}

      {canEdit && !isCompleted && (
        <div className="grid gap-3 md:grid-cols-2">
          {!hasPre && <InspectionForm phase="pre" jobId={job.id} onDone={refresh} defaultName={user?.name} />}
          {hasPre && !hasPost && (
            <div className="md:col-span-2">
              <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 mb-3">
                ✅ <b>Partenza inviata.</b> Al rientro compila i dati del ritorno (km attuali + € diesel speso).
              </div>
              <InspectionForm phase="post" jobId={job.id} onDone={refresh} defaultName={user?.name} />
            </div>
          )}
          {hasPre && hasPost && (
            <div className="md:col-span-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
              ✅ <b>Ispezioni complete.</b> Partenza + Ritorno già inviati.
            </div>
          )}
        </div>
      )}
      {!canEdit && inspections.length === 0 && (
        <div className="text-sm text-slate-400 italic text-center py-6 border border-dashed border-slate-200 rounded-xl">
          Nessuna ispezione registrata.
        </div>
      )}
    </section>
  );
}
